import './view-job.scss';

import { Box } from '@mui/material';
import { viewJobServiceStore } from 'mobx/job/ViewJobServiceStore';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useFetchSingleData } from 'services/common/react-query-common-service';
import { useAppSelector } from 'store/hooks';

import StylishLoader from '../components/StylishLoader';
import { JobQueueTableType } from '../enum/TeamUserRoles';
import JobContent from './components/JobContent';
import JobHeader from './components/JobHeader';
import RightSideTab from './components/RightSideTab';

interface ViewJobLocationState {
  Params?: {
    jobId?: string;
    score?: number | string;
    tableType?: JobQueueTableType;
  };
}

const ViewJobV2: React.FC = observer(() => {
  const params = useParams();
  const location = useLocation();
  const state = location.state as ViewJobLocationState;
  const jobId =
    params?.id || state?.Params?.jobId || viewJobServiceStore.jobInfo?.id;

  const getTableType = () => {
    if (state?.Params?.tableType) return state.Params.tableType;
    const path = location.pathname.toLowerCase();
    if (path.includes('/recruiter/'))
      return JobQueueTableType.RECRUITER_PENDING;
    if (path.includes('/business-head/'))
      return JobQueueTableType.BUSINESS_HEAD_ASSIGNED;
    if (path.includes('/management/'))
      return JobQueueTableType.MANAGEMENT_JQ_UNALLOCATED;
    if (path.includes('/manager/')) return JobQueueTableType.MANAGER_ASSIGNED;
    if (path.includes('/support/')) return JobQueueTableType.SUPPORT_PENDING;
    if (path.includes('/team-lead/'))
      return JobQueueTableType.TEAM_LEAD_ASSIGNED;
    return undefined;
  };

  const tableType = getTableType();
  const initialScore = String(state?.Params?.score || '');
  const [score, setScore] = useState(initialScore);

  const { userInfo: userDetail }: any = useAppSelector(
    (store: any) => store.userLogin,
  );

  // Fetch job data using React Query
  const { data: jobData, refetch } = useFetchSingleData<any>(
    ['job-posting', jobId, Number(score)],
    '', // Pass empty string to id to handle query params manually in endpoint
    'job-postings/job-queue?score=' +
      Number(score) +
      (jobId ? '&id=' + (jobId as string) : ''),
    {
      enabled: !!jobId,
      refetchOnMount: 'always',
    },
  );

  // Update MobX store when API data arrives
  useEffect(() => {
    if (jobData?.data?.[0]) {
      viewJobServiceStore.setJobInfo(jobData.data[0]);
      viewJobServiceStore.setScore(Number(score));
    }
  }, [jobData, score]);

  // Refetch when score changes
  useEffect(() => {
    if (score !== initialScore) {
      refetch();
    }
  }, [score, initialScore, refetch]);

  // Show loader ONLY if jobInfo is empty (first time or cleared)
  if (!viewJobServiceStore.jobInfo) {
    return <StylishLoader text="Loading job details..." />;
  }

  return (
    <Box
      className="view-job-container"
      data-job-id={jobId}
      data-table-type={tableType}>
      <Box className="job-details-section full-width">
        <JobHeader
          tableType={tableType}
          jobInfo={viewJobServiceStore.jobInfo}
          userDetail={userDetail}
          refetch={refetch}
        />
        <JobContent
          score={score}
          tableType={tableType}
          jobInfo={viewJobServiceStore.jobInfo}
          refetch={refetch}
        />
      </Box>
      <RightSideTab
        score={score}
        refetch={refetch}
        tableType={tableType}
        jobId={jobId}
        jobInfo={viewJobServiceStore.jobInfo}
        onScoreChange={setScore}
      />
    </Box>
  );
});

export default ViewJobV2;
